/**
 * AdminMarkets — /admin/markets
 * Event list with search/filter by sport. Click event → market list.
 * Each market: editable odds, Suspend/Resume/Settle buttons.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  adminListEvents,
  adminMarketAction,
  adminUpdateOdds,
  adminPromoteEvent,
  adminCreateEventFlyerAI,
  adminCreateEventFlyerPIL,
  adminListEventFlyers,
  adminUpdateEventFlyer,
  adminDeleteEventFlyer,
  adminPushEventMarketing,
  type AdminEventItem,
  type AdminMarketItem,
  type AdminFlyerOut,
} from '../../services/api';

interface AdminCtx {
  adminEmail: string | null;
}

function MarketRow({
  market,
  email,
  onRefresh,
}: {
  market: AdminMarketItem;
  email: string;
  onRefresh: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oddsInputs, setOddsInputs] = useState<Record<string, string>>(
    Object.fromEntries(market.selections.map((s) => [s.id, String(s.odds_decimal)])),
  );
  const [settleScore, setSettleScore] = useState({ home: '', away: '' });
  const [showSettle, setShowSettle] = useState(false);

  const doAction = async (action: 'suspend' | 'resume' | 'settle') => {
    setLoading(true);
    setError(null);
    try {
      if (action === 'settle') {
        const hs = parseInt(settleScore.home, 10);
        const as_ = parseInt(settleScore.away, 10);
        if (isNaN(hs) || isNaN(as_)) {
          setError('Enter valid scores.');
          return;
        }
        await adminMarketAction(email, market.market_id, 'settle', hs, as_);
      } else {
        await adminMarketAction(email, market.market_id, action);
      }
      onRefresh();
    } catch {
      setError('Action failed. Try again.');
    } finally {
      setLoading(false);
      setShowSettle(false);
    }
  };

  const saveOdds = async (selId: string) => {
    const val = parseFloat(oddsInputs[selId]);
    if (isNaN(val) || val <= 1) return;
    setLoading(true);
    setError(null);
    try {
      await adminUpdateOdds(email, market.market_id, selId, val);
      onRefresh();
    } catch {
      setError('Odds update failed.');
    } finally {
      setLoading(false);
    }
  };

  const isSuspended = market.status === 'suspended';

  return (
    <div
      className={`mb-4 rounded-xl border p-4 ${
        isSuspended ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="font-semibold text-gray-800">{market.name}</span>{' '}
          <span className="text-xs text-gray-400 ml-1">({market.market_type})</span>
          {isSuspended && (
            <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600">
              SUSPENDED
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {market.status === 'open' && (
            <button
              disabled={loading}
              onClick={() => doAction('suspend')}
              className="rounded-lg bg-yellow-100 px-3 py-1.5 text-xs font-medium text-yellow-800 hover:bg-yellow-200 disabled:opacity-60"
            >
              Suspend
            </button>
          )}
          {isSuspended && (
            <button
              disabled={loading}
              onClick={() => doAction('resume')}
              className="rounded-lg bg-green-100 px-3 py-1.5 text-xs font-medium text-green-800 hover:bg-green-200 disabled:opacity-60"
            >
              Resume
            </button>
          )}
          {market.status !== 'settled' && market.status !== 'cancelled' && (
            <button
              disabled={loading}
              onClick={() => setShowSettle((v) => !v)}
              className="rounded-lg bg-violet-100 px-3 py-1.5 text-xs font-medium text-violet-800 hover:bg-violet-200 disabled:opacity-60"
            >
              Settle
            </button>
          )}
        </div>
      </div>

      {showSettle && (
        <div className="mt-3 flex items-center gap-2">
          <input
            type="number"
            min="0"
            placeholder="Home"
            value={settleScore.home}
            onChange={(e) => setSettleScore((s) => ({ ...s, home: e.target.value }))}
            className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
          />
          <span className="text-gray-400">-</span>
          <input
            type="number"
            min="0"
            placeholder="Away"
            value={settleScore.away}
            onChange={(e) => setSettleScore((s) => ({ ...s, away: e.target.value }))}
            className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
          />
          <button
            onClick={() => doAction('settle')}
            disabled={loading}
            className="rounded-lg bg-violet-600 px-3 py-1 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-60"
          >
            Confirm settle
          </button>
          <button
            onClick={() => setShowSettle(false)}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Cancel
          </button>
        </div>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      {/* Selections with editable odds */}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {market.selections.map((sel) => (
          <div key={sel.id} className="flex items-center gap-2 rounded bg-gray-50 px-3 py-2">
            <span className="flex-1 text-sm text-gray-700 truncate">{sel.name}</span>
            <input
              type="number"
              step="0.01"
              min="1.01"
              value={oddsInputs[sel.id] ?? String(sel.odds_decimal)}
              onChange={(e) =>
                setOddsInputs((prev) => ({ ...prev, [sel.id]: e.target.value }))
              }
              className="w-20 rounded border border-gray-300 px-2 py-0.5 text-sm"
            />
            <button
              onClick={() => saveOdds(sel.id)}
              disabled={loading}
              className="text-xs font-medium text-violet-600 hover:text-violet-800 disabled:opacity-40"
            >
              Save
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Per-event flyer lifecycle panel ──────────────────────────────────────

const POLL_INTERVAL_MS = 4000;
const POLL_MAX_TICKS = 15; // ~60s cap

/** Parse a FastAPI error into a readable string (detail may be a string or 422 array). */
function flyerErrorMessage(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 503) {
    return 'AI flyer generation needs Vertex AI credentials. Ask an administrator to configure them.';
  }
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : null))
      .filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  return 'Action failed. Please try again.';
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'generating':
      return 'bg-orange-100 text-orange-700 animate-pulse';
    case 'draft':
      return 'bg-gray-100 text-gray-600';
    case 'approved':
      return 'bg-green-100 text-green-700';
    case 'archived':
      return 'bg-gray-100 text-gray-400';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function methodChip(method: string): string {
  switch (method) {
    case 'ai_generated':
      return 'AI';
    case 'pil':
      return 'PIL';
    case 'uploaded':
      return 'Upload';
    default:
      return method;
  }
}

function FlyerCard({
  flyer,
  eventId,
  email,
  onChanged,
  onToast,
}: {
  flyer: AdminFlyerOut;
  eventId: string;
  email: string;
  onChanged: () => void;
  onToast: (msg: string) => void;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);

  const doUpdate = async (
    status: 'approved' | 'archived' | 'draft',
    label: string,
    successMsg: string,
  ) => {
    setPending(label);
    setCardError(null);
    try {
      await adminUpdateEventFlyer(email, eventId, flyer.id, { status });
      onToast(successMsg);
      onChanged();
    } catch (err) {
      setCardError(flyerErrorMessage(err));
    } finally {
      setPending(null);
    }
  };

  const doDelete = async () => {
    if (!window.confirm('Delete this flyer? This cannot be undone.')) return;
    setPending('Deleting…');
    setCardError(null);
    try {
      await adminDeleteEventFlyer(email, eventId, flyer.id);
      onToast('Flyer deleted.');
      onChanged();
    } catch (err) {
      setCardError(flyerErrorMessage(err));
    } finally {
      setPending(null);
    }
  };

  const downloadUrl = `/api/v1/events/${eventId}/flyers/${flyer.id}/personalised?ref=PROMO`;
  const busy = pending !== null;
  const btn =
    'min-h-[44px] rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50';

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Image — portrait 9:16 */}
      <div className="relative flex aspect-[9/16] items-center justify-center overflow-hidden bg-gray-50">
        {flyer.status === 'generating' ? (
          <div className="flex flex-col items-center gap-2 text-orange-600">
            <svg className="h-8 w-8 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-xs font-medium">Generating image…</span>
          </div>
        ) : flyer.image_url ? (
          <img
            src={flyer.image_url}
            alt="Event flyer"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="shimmer absolute inset-0" aria-hidden="true" />
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col gap-2 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(flyer.status)}`}
          >
            {flyer.status}
          </span>
          <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-500">
            {methodChip(flyer.generation_method)}
          </span>
        </div>

        {cardError && <p className="text-xs text-red-600">{cardError}</p>}

        {flyer.status === 'generating' ? (
          <p className="text-xs text-gray-400">Generating…</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {flyer.status === 'draft' && (
              <>
                <button
                  type="button"
                  onClick={() => doUpdate('approved', 'Approving…', 'Flyer approved.')}
                  disabled={busy || !flyer.image_url}
                  className={`${btn} bg-green-100 text-green-800 hover:bg-green-200`}
                  title={!flyer.image_url ? 'No image to approve' : undefined}
                >
                  {pending === 'Approving…' ? 'Approving…' : 'Approve'}
                </button>
                <button
                  type="button"
                  onClick={doDelete}
                  disabled={busy}
                  className={`${btn} bg-red-100 text-red-700 hover:bg-red-200`}
                >
                  {pending === 'Deleting…' ? 'Deleting…' : 'Delete'}
                </button>
              </>
            )}
            {flyer.status === 'approved' && (
              <>
                <button
                  type="button"
                  onClick={() => doUpdate('archived', 'Archiving…', 'Flyer archived.')}
                  disabled={busy}
                  className={`${btn} bg-gray-100 text-gray-700 hover:bg-gray-200`}
                >
                  {pending === 'Archiving…' ? 'Archiving…' : 'Archive'}
                </button>
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`${btn} inline-flex items-center bg-violet-600 text-white hover:bg-violet-700`}
                >
                  Download
                </a>
              </>
            )}
            {flyer.status === 'archived' && (
              <>
                <button
                  type="button"
                  onClick={() => doUpdate('draft', 'Restoring…', 'Flyer restored to draft.')}
                  disabled={busy}
                  className={`${btn} bg-gray-100 text-gray-700 hover:bg-gray-200`}
                >
                  {pending === 'Restoring…' ? 'Restoring…' : 'Restore'}
                </button>
                <button
                  type="button"
                  onClick={doDelete}
                  disabled={busy}
                  className={`${btn} bg-red-100 text-red-700 hover:bg-red-200`}
                >
                  {pending === 'Deleting…' ? 'Deleting…' : 'Delete'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EventFlyersPanel({ ev, email }: { ev: AdminEventItem; email: string }) {
  const [open, setOpen] = useState(false);
  const [flyers, setFlyers] = useState<AdminFlyerOut[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [pushing, setPushing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const loadFlyers = useCallback(async () => {
    try {
      const res = await adminListEventFlyers(email, ev.event_id);
      setFlyers(res.data);
      setLoaded(true);
      setLoadError(null);
    } catch {
      setLoaded(true);
      setLoadError('Could not load flyers.');
    }
  }, [email, ev.event_id]);

  // Cleanup timers on unmount
  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
      stopPolling();
    },
    [stopPolling],
  );

  const toggleOpen = () => {
    setOpen((wasOpen) => {
      const next = !wasOpen;
      if (next && !loaded) {
        // Fetch the first time the panel is expanded. A prior failed load
        // leaves `loaded` true with an error banner; the inline "Retry"
        // button re-runs loadFlyers without needing to re-open.
        void loadFlyers();
      }
      if (!next) {
        // Stop background polling when the admin collapses the panel.
        stopPolling();
      }
      return next;
    });
  };

  /** Poll until no flyer is `generating` (capped). */
  const startPolling = useCallback(() => {
    if (pollTimer.current) return;
    let ticks = 0;
    pollTimer.current = setInterval(async () => {
      ticks += 1;
      try {
        const res = await adminListEventFlyers(email, ev.event_id);
        setFlyers(res.data);
        const stillGenerating = res.data.some((f) => f.status === 'generating');
        if (!stillGenerating || ticks >= POLL_MAX_TICKS) stopPolling();
      } catch {
        if (ticks >= POLL_MAX_TICKS) stopPolling();
      }
    }, POLL_INTERVAL_MS);
  }, [email, ev.event_id, stopPolling]);

  const handleGeneratePIL = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      await adminCreateEventFlyerPIL(email, ev.event_id);
      showToast('PIL flyer created.');
      await loadFlyers();
    } catch (err) {
      setCreateError(flyerErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const handleGenerateAI = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      await adminCreateEventFlyerAI(email, ev.event_id, prompt.trim() || undefined);
      showToast('AI generation started…');
      setShowPrompt(false);
      setPrompt('');
      await loadFlyers();
      startPolling();
    } catch (err) {
      setCreateError(flyerErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const handlePush = async () => {
    setPushing(true);
    try {
      await adminPushEventMarketing(email, ev.event_id);
      showToast('Event pushed to marketing service.');
    } catch (err) {
      showToast(flyerErrorMessage(err));
    } finally {
      setPushing(false);
    }
  };

  const btnAction =
    'min-h-[44px] rounded-lg px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-60';

  return (
    <div>
      <button
        type="button"
        onClick={toggleOpen}
        className="flex items-center gap-1 text-sm font-medium text-violet-600 hover:text-violet-800"
      >
        <span className={`transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
        Flyers
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleGeneratePIL}
              disabled={creating}
              className={`${btnAction} bg-violet-100 text-violet-700 hover:bg-violet-200`}
              title="Generate a flyer with the built-in PIL renderer"
            >
              🎨 Generate (PIL)
            </button>
            <button
              type="button"
              onClick={() => setShowPrompt((v) => !v)}
              disabled={creating}
              className={`${btnAction} bg-indigo-100 text-indigo-700 hover:bg-indigo-200`}
              title="Generate a flyer with Vertex AI"
            >
              🤖 AI Generate
            </button>
            <button
              type="button"
              onClick={handlePush}
              disabled={pushing}
              className={`${btnAction} bg-orange-100 text-orange-700 hover:bg-orange-200`}
              title="Push event to the marketing service"
            >
              {pushing ? 'Pushing…' : '📢 Push to Marketing'}
            </button>
          </div>

          {/* AI prompt */}
          {showPrompt && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <textarea
                rows={3}
                maxLength={2000}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Optional prompt — describe the flyer (leave blank for an auto-built prompt)…"
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
              />
              <div className="mt-2 flex items-center justify-end gap-2">
                <span className="mr-auto text-xs text-gray-400">{prompt.length}/2000</span>
                <button
                  type="button"
                  onClick={() => {
                    setShowPrompt(false);
                    setPrompt('');
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleGenerateAI}
                  disabled={creating}
                  className="min-h-[44px] rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
                >
                  {creating ? 'Starting…' : 'Generate'}
                </button>
              </div>
            </div>
          )}

          {createError && <p className="text-xs text-red-600">{createError}</p>}
          {toast && (
            <p className="rounded-lg bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700">
              {toast}
            </p>
          )}
          {loadError && (
            <p className="flex items-center gap-2 text-xs text-red-600">
              {loadError}
              <button
                type="button"
                onClick={loadFlyers}
                className="font-semibold text-violet-600 underline hover:text-violet-800"
              >
                Retry
              </button>
            </p>
          )}

          {/* Flyer grid */}
          {loaded && flyers.length === 0 && !loadError ? (
            <p className="text-xs text-gray-500">No flyers yet. Generate one above.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {flyers.map((flyer) => (
                <FlyerCard
                  key={flyer.id}
                  flyer={flyer}
                  eventId={ev.event_id}
                  email={email}
                  onChanged={loadFlyers}
                  onToast={showToast}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminMarkets() {
  const { adminEmail } = useOutletContext<AdminCtx>();
  const [events, setEvents] = useState<AdminEventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sportSlug, setSportSlug] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<AdminEventItem | null>(null);

  const loadEvents = async () => {
    if (!adminEmail) return;
    setLoading(true);
    try {
      const res = await adminListEvents(adminEmail, sportSlug || undefined, search || undefined);
      setEvents(res.data);
      if (selectedEvent) {
        const updated = res.data.find((e) => e.event_id === selectedEvent.event_id);
        if (updated) setSelectedEvent(updated);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminEmail) loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminEmail, sportSlug]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadEvents();
  };

  const handlePromote = async (eventId: string) => {
    if (!adminEmail) return;
    try {
      await adminPromoteEvent(adminEmail, eventId);
    } catch {
      // best-effort
    }
  };

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Markets</h1>

      {/* Filters */}
      <form onSubmit={handleSearch} className="mb-6 flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search teams..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-violet-500 focus:outline-none"
        />
        <input
          type="text"
          placeholder="Sport slug (e.g. football)"
          value={sportSlug}
          onChange={(e) => setSportSlug(e.target.value)}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-violet-500 focus:outline-none"
        />
        <button
          type="submit"
          className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
        >
          Search
        </button>
      </form>

      {loading && (
        <p className="text-sm text-gray-400">Loading...</p>
      )}

      {!selectedEvent ? (
        /* Event list */
        <div className="space-y-3">
          {events.length === 0 && !loading && (
            <p className="text-sm text-gray-500">No events found.</p>
          )}
          {events.map((ev) => (
            <div
              key={ev.event_id}
              className="rounded-xl border border-gray-200 bg-white p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800">
                    {ev.home_team} vs {ev.away_team}
                  </p>
                  <p className="text-xs text-gray-400">
                    {ev.sport_slug} · {new Date(ev.starts_at).toLocaleDateString('en-ZA')} ·{' '}
                    <span className="capitalize">{ev.status}</span> ·{' '}
                    {ev.markets.length} market{ev.markets.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => handlePromote(ev.event_id)}
                    className="rounded-lg bg-pink-100 px-3 py-1.5 text-xs font-medium text-pink-700 hover:bg-pink-200"
                  >
                    Promote
                  </button>
                  <button
                    onClick={() => setSelectedEvent(ev)}
                    className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700"
                  >
                    Manage markets
                  </button>
                </div>
              </div>
              {/* Flyer lifecycle + marketing actions */}
              {adminEmail && (
                <div className="mt-3 border-t border-gray-100 pt-3">
                  <EventFlyersPanel ev={ev} email={adminEmail} />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* Market detail */
        <div>
          <button
            onClick={() => setSelectedEvent(null)}
            className="mb-4 text-sm text-violet-600 hover:text-violet-800"
          >
            &larr; Back to events
          </button>
          <h2 className="mb-4 text-lg font-bold text-gray-800">
            {selectedEvent.home_team} vs {selectedEvent.away_team}
          </h2>
          {adminEmail &&
            selectedEvent.markets.map((m) => (
              <MarketRow
                key={m.market_id}
                market={m}
                email={adminEmail}
                onRefresh={loadEvents}
              />
            ))}
          {selectedEvent.markets.length === 0 && (
            <p className="text-sm text-gray-500">No markets for this event.</p>
          )}
        </div>
      )}
    </div>
  );
}
